import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ScrollText, Search, ChevronDown, BookOpenText, ArrowRight, Landmark, Table2 } from "lucide-react";
import api from "@/lib/api";

// Opsommingen, genummerde artikelleden en vraagkopjes uit de cao-bron op eigen regels.
// Conservatieve patronen: geen splitsing bij bedragen (115,-), datums (1-10-'24) of
// wetsartikelen (7:629) — die bevatten nooit "cijfer. spatie Hoofdletter".
const formatBron = (t) => (t || "")
  .replace(/\s-\s(?=[A-Za-zÀ-ü€\d])/g, "\n• ")
  .replace(/(?<=[a-zà-ü.;:\)]) (?=\d{1,2}\. [A-ZÀ-Ü])/g, "\n")
  .replace(/(?<=[a-zà-ü.!?\)]) (?=[A-ZÀ-Ü][^.!?\n]{4,60}\? )/g, "\n\n")
  .replace(/\s(?=Functiegroep \d+ \(ORBA)/g, "\n\n");
const isVraagKop = (r) => r.endsWith("?") && r.length < 70 && !r.startsWith("•");

const BronBlok = ({ tekst, pagina, testid }) => (
  <div data-testid={testid} className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-4">
    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
      {formatBron(tekst).split("\n").map((r, i) => (
        isVraagKop(r)
          ? <span key={i} className="block font-bold text-slate-800">{r}</span>
          : <span key={i}>{r}{"\n"}</span>
      ))}
    </p>
    <p className="text-[11px] text-slate-500 mt-3 font-medium">
      Bron: CAO Zoetwarenindustrie 2024–2026{pagina ? `, pagina ${pagina}` : ""}
    </p>
  </div>
);

// Artikel 3.2: gebruikersweergave per functiegroep (kop zonder ORBA-details;
// de volledige letterlijke tekst blijft in het bronblok).
const parseFunctiegroepen = (t) => {
  const m = [...(t || "").matchAll(/Functiegroep (\d+) \(ORBA-bandbreedte [\d–-]+\):\s*([^]*?)(?=Functiegroep \d+ \(|Toelichting:|$)/g)];
  return m.map((x) => ({ nr: x[1], functies: x[2].replace(/\.\s*$/, "").split(";").map((f) => f.trim().replace(/\.$/, "")).filter(Boolean) }));
};

const FunctiegroepenLijst = ({ kaart }) => {
  const groepen = parseFunctiegroepen(kaart.brontekst);
  const nok = kaart.nok_functies || {};
  if (groepen.length === 0) return null;
  return (
    <div data-testid="cao-functiegroepen-3.2" className="space-y-2.5">
      {groepen.map((g) => (
        <div key={g.nr}>
          <p className="text-xs font-bold text-slate-800">Functiegroep {g.nr}</p>
          <p className="text-xs text-slate-600 leading-relaxed">{g.functies.join(" · ")}</p>
          {nok[g.nr] && (
            <div data-testid={`cao-nok-functies-groep-${g.nr}`}
              className="mt-1.5 bg-[#FDF8EE] border border-[#EBD9BC] rounded-md px-2.5 py-2">
              <p className="text-[11px] font-semibold text-[#8a5a20]">Afgeleide functies uit het Functiehandboek (NOK)</p>
              <p className="text-xs text-slate-700 leading-relaxed mt-0.5">{nok[g.nr].join(" · ")}</p>
            </div>
          )}
        </div>
      ))}
      {kaart.nok_uitleg && (
        <p data-testid="cao-nok-uitleg-3.2" className="text-[11px] text-slate-500 leading-relaxed pt-1">
          {kaart.nok_uitleg}
        </p>
      )}
    </div>
  );
};

const TabelBlok = ({ tabel, code }) => {
  if (tabel.type === "bedragen") {
    return (
      <div data-testid={`cao-tabel-${code}`} className="overflow-x-auto">
        <table className="w-full text-xs border border-slate-200 rounded-lg">
          <thead><tr className="bg-slate-50">
            {tabel.koppen.map((k, i) => <th key={i} className="text-left font-semibold text-slate-700 px-2.5 py-1.5 border-b border-slate-200">{k}</th>)}
          </tr></thead>
          <tbody>
            {tabel.rijen.map((r, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="px-2.5 py-1.5 text-slate-700">{r.label}</td>
                {r.waarden.map((w, j) => <td key={j} className="px-2.5 py-1.5 text-slate-700 whitespace-nowrap">{w}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <div data-testid={`cao-tabel-${code}`} className="space-y-1.5">
      <ul className="text-xs text-slate-700 leading-relaxed list-disc pl-4 space-y-1.5">
        {tabel.rijen.map((r, i) => <li key={i}>{r}</li>)}
      </ul>
      <p className="text-xs text-slate-600 leading-relaxed">{tabel.staart}</p>
    </div>
  );
};

const NotitieTekst = ({ kaart }) => {
  const links = kaart.notitie_links || {};
  const delen = kaart.notitie.split(/((?:[Zz]ie )?artikel \d+\.\d+)/g);
  return (
    <p className="text-xs text-slate-700 leading-relaxed">
      {delen.map((d, i) => {
        const m = d.match(/^([Zz]ie )?artikel (\d+\.\d+)$/);
        if (m && links[m[2]]) {
          return (
            <Link key={i} to={`/cao?artikel=${m[2]}`} data-testid={`cao-notitie-link-${kaart.code}-${m[2]}`}
              className="font-medium text-[#B9772B] underline underline-offset-2 hover:text-[#8a5a20]">
              {`${m[1] ? "Zie " : ""}artikel ${m[2]} – ${links[m[2]]}`}
            </Link>
          );
        }
        return <span key={i}>{d}</span>;
      })}
    </p>
  );
};

const ArtikelKaart = ({ kaart, open, onToggle }) => {
  const [bronOpen, setBronOpen] = useState(false);
  return (
    <div id={`cao-art-${kaart.code}`} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <button data-testid={`cao-artikel-toggle-${kaart.code}`} onClick={onToggle}
        className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
        <div>
          <p className="text-sm font-semibold text-slate-800">{kaart.vraag_titel}</p>
          <p className="text-xs text-slate-500 mt-0.5">Artikel {kaart.code} · {kaart.officiele_titel}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`cao-artikel-detail-${kaart.code}`} className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          <p className="text-sm text-slate-700 leading-relaxed">{kaart.uitleg}</p>
          {kaart.code === "3.2" && <FunctiegroepenLijst kaart={kaart} />}
          {kaart.tabel && <TabelBlok tabel={kaart.tabel} code={kaart.code} />}
          {kaart.notitie && (
            <div data-testid={`cao-notitie-${kaart.code}`} className="bg-[#FDF8EE] border border-[#EBD9BC] rounded-lg px-3 py-2.5">
              <p className="text-[11px] font-semibold text-[#8a5a20] uppercase tracking-wide mb-1">Praktische uitleg</p>
              <NotitieTekst kaart={kaart} />
            </div>
          )}
          {kaart.link && (
            <Link to={kaart.link.route} data-testid={`cao-link-${kaart.code}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#B9772B] hover:underline">
              {kaart.link.label} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
          {kaart.bijlage_link && (
            <div>
              <Link to={`/cao?bijlage=${kaart.bijlage_link.id}`} data-testid={`cao-artikel-bijlage-link-${kaart.code}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#B9772B] hover:underline">
                Lees {kaart.bijlage_link.label} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
          <div>
            <button data-testid={`cao-bron-btn-${kaart.code}`} onClick={() => setBronOpen(!bronOpen)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors">
              <BookOpenText className="w-3.5 h-3.5" /> Waar staat dat? {bronOpen ? "Verberg" : "Toon"} de volledige cao-tekst
            </button>
            {bronOpen && (kaart.bron_blokken
              ? <BronBlokken blokken={kaart.bron_blokken} pagina={kaart.pagina} testid={`cao-brontekst-${kaart.code}`} />
              : <BronBlok tekst={kaart.brontekst} pagina={kaart.pagina} testid={`cao-brontekst-${kaart.code}`} />)}
          </div>
        </div>
      )}
    </div>
  );
};

const BronBlokken = ({ blokken, pagina, testid }) => (
  <div data-testid={testid} className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2.5">
    {blokken.map((b, i) => {
      if (b.type === "kop") return <p key={i} className="text-xs font-bold text-slate-800">{b.tekst}</p>;
      if (b.type === "subkop") return <p key={i} className="text-xs font-bold text-slate-800 pt-1.5">{b.tekst}</p>;
      if (b.type === "opsomming") {
        return (
          <ul key={i} className="text-xs text-slate-700 leading-relaxed list-disc pl-4 space-y-1">
            {b.items.map((it, j) => <li key={j}>{it}</li>)}
          </ul>
        );
      }
      if (b.type === "tabel") {
        return (
          <div key={i} className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-100">
                {b.kolommen.map((k, j) => <th key={j} className="text-left font-semibold text-slate-700 px-2.5 py-1.5 border-b border-slate-200">{k}</th>)}
              </tr></thead>
              <tbody>
                {b.rijen.map((r, j) => (
                  <tr key={j} className="border-b border-slate-100 last:border-0">
                    {r.map((c, x) => <td key={x} className="px-2.5 py-1.5 text-slate-700 align-top">{c}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      return (
        <p key={i} className="text-xs text-slate-700 leading-relaxed">
          {b.zinnen.map((z, j) => (
            isVraagKop(z)
              ? <span key={j} className="block font-bold text-slate-800">{z}</span>
              : <span key={j} className="block">{z}</span>
          ))}
        </p>
      );
    })}
    <p className="text-[11px] text-slate-500 pt-1 font-medium">
      Bron: CAO Zoetwarenindustrie 2024–2026{pagina ? `, pagina ${pagina}` : ""}
    </p>
  </div>
);

const MonoBlok = ({ tekst, testid }) => (
  <pre data-testid={testid} className="text-[11px] font-mono text-slate-700 bg-slate-100 border border-slate-200 rounded-lg p-2.5 overflow-x-auto whitespace-pre-wrap leading-relaxed">{tekst}</pre>
);

const KenmerkenTabel = ({ velden, testid }) => (
  <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid={testid}>
    <table className="w-full text-xs">
      <tbody>
        {velden.map(([k, v]) => (
          <tr key={k} className="border-b border-slate-100 last:border-0">
            <td className="px-2.5 py-1.5 font-medium text-slate-600 whitespace-nowrap align-top">{k}</td>
            <td className="px-2.5 py-1.5 text-slate-800">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SectieTekst = ({ tekst }) => (
  <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
    {formatBron(tekst).split("\n").map((r, i) => (
      isVraagKop(r)
        ? <span key={i} className="block font-bold text-slate-800">{r}</span>
        : <span key={i}>{r}{"\n"}</span>
    ))}
  </p>
);

const B6Regels = ({ regels, testid }) => (
  <div data-testid={testid} className="space-y-1.5 text-xs text-slate-700 leading-relaxed">
    {regels.map((r, i) => {
      if (/^Stap \d[:\s]/.test(r)) return <p key={i} className="font-bold text-slate-800 pt-2.5">{r}</p>;
      if (r.startsWith("◼ ")) {
        const t = r.slice(2);
        return t.length <= 70
          ? <p key={i} className="font-bold text-slate-800 pt-2.5">{t}</p>
          : <p key={i} className="pt-2.5">{t}</p>;
      }
      if (r.startsWith("• ")) {
        const t = r.slice(2);
        return <p key={i} className={`pl-4 ${t.endsWith(":") && t.length < 48 ? "font-semibold text-slate-800 pt-1.5" : ""}`}>• {t}</p>;
      }
      if (r.startsWith("o ")) return <p key={i} className="pl-8">– {r.slice(2)}</p>;
      if (r.startsWith("▪ ")) return <p key={i} className="pl-12">· {r.slice(2)}</p>;
      const upper = r === r.toUpperCase() && /[A-Z]{4}/.test(r);
      const kort = r.length <= 48 && !/[.!?]$/.test(r);
      const kop = upper || kort || (r.endsWith(":") && r.length < 48);
      return <p key={i} className={kop ? "font-semibold text-slate-800 pt-1.5" : ""}>{r}</p>;
    })}
  </div>
);

const BijlageSectie = ({ sectie, bijlageId, index }) => {
  const [open, setOpen] = useState(false);
  const inhoud = sectie.type === "voorbeeld" ? (
    <div className="space-y-2">
      {sectie.beschrijving && <SectieTekst tekst={sectie.beschrijving} />}
      {sectie.blokken.map((blok, i) => (
        <MonoBlok key={i} tekst={blok} testid={`cao-bijlage-${bijlageId}-mono-${index}-${i}`} />
      ))}
      {sectie.berekening && <SectieTekst tekst={sectie.berekening} />}
      {sectie.kenmerken && <KenmerkenTabel velden={sectie.kenmerken} testid={`cao-bijlage-${bijlageId}-kenmerken-${index}`} />}
      {sectie.naschrift && <p className="text-[11px] text-slate-500 italic">{sectie.naschrift}</p>}
    </div>
  ) : sectie.regels_b6 ? (
    <B6Regels regels={sectie.regels_b6} testid={`cao-b6-regels-${index}`} />
  ) : sectie.regels ? (
    <div className="space-y-1.5">
      <SectieTekst tekst={sectie.regels[0]} />
      <ul className="text-xs text-slate-700 leading-relaxed list-disc pl-4 space-y-1">
        {sectie.regels.slice(1).map((r, i) => <li key={i}>{r}</li>)}
      </ul>
    </div>
  ) : <SectieTekst tekst={sectie.tekst} />;

  if (!sectie.kop) return <div>{inhoud}</div>;
  if (!sectie.inklapbaar) {
    return (
      <div>
        <p className="text-xs font-bold text-slate-800 mb-1">{sectie.kop}</p>
        {inhoud}
      </div>
    );
  }
  return (
    <div className="border border-slate-200 rounded-lg bg-white">
      <button data-testid={`cao-bijlage-${bijlageId}-sectie-${index}`} onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50">
        <span>{sectie.kop}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-3 pb-3">{inhoud}</div>}
    </div>
  );
};

const BijlageWeergave = ({ b }) => {
  const [filter, setFilter] = useState(null);
  const isB4 = b.id === "b4";
  const soorten = isB4
    ? [...new Set(b.weergave.secties
        .filter((s) => s.type === "voorbeeld")
        .map((s) => (s.kop.match(/(Dagdienst|\w+ploegendienst)/) || [])[1])
        .filter(Boolean))]
    : [];
  const zichtbaar = (s) =>
    !filter || s.type !== "voorbeeld" || s.kop.includes(filter);
  return (
    <div className="space-y-2.5" data-testid={`cao-bijlage-weergave-${b.id}`}>
      {isB4 && soorten.length > 1 && (
        <div className="flex flex-wrap gap-1.5" data-testid="cao-b4-roosterfilter">
          <span className="text-[11px] text-slate-500 self-center">Toon voorbeelden voor:</span>
          <button data-testid="cao-b4-filter-alle" onClick={() => setFilter(null)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${!filter ? "bg-[#B9772B] text-white border-[#B9772B]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
            Alle
          </button>
          {soorten.map((s) => (
            <button key={s} data-testid={`cao-b4-filter-${s.toLowerCase()}`} onClick={() => setFilter(filter === s ? null : s)}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${filter === s ? "bg-[#B9772B] text-white border-[#B9772B]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
              {s}
            </button>
          ))}
        </div>
      )}
      {b.weergave.secties.map((s, i) =>
        zichtbaar(s) ? <BijlageSectie key={i} sectie={s} bijlageId={b.id} index={i} /> : null
      )}
    </div>
  );
};

const BijlageKaart = ({ b, open, onToggle }) => {
  const [bronOpen, setBronOpen] = useState(false);
  return (
    <div id={`cao-bijlage-${b.id}`} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <button data-testid={`cao-bijlage-toggle-${b.id}`} onClick={onToggle}
        className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
        <div>
          <p className="text-sm font-semibold text-slate-800">{b.vraag_titel}</p>
          <p className="text-xs text-slate-500 mt-0.5">Bijlage {b.nummer} · {b.titel}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`cao-bijlage-detail-${b.id}`} className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          <p className="text-sm text-slate-700 leading-relaxed">{b.uitleg}</p>
          {b.weergave && <BijlageWeergave b={b} />}
          <div>
            <button data-testid={`cao-bijlage-bron-btn-${b.id}`} onClick={() => setBronOpen(!bronOpen)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors">
              <BookOpenText className="w-3.5 h-3.5" /> Waar staat dat? {bronOpen ? "Verberg" : "Toon"} de volledige cao-tekst
            </button>
            {bronOpen && <BronBlok tekst={b.brontekst} pagina={b.pagina} testid={`cao-bijlage-bron-${b.id}`} />}
          </div>
        </div>
      )}
    </div>
  );
};

const B1Matrix = ({ tabel, pagina, index }) => (
  <div className="space-y-2" data-testid={`cao-bijlage1-matrix-${index}`}>
    <p className="text-xs text-slate-600 leading-relaxed">{tabel.intro.replace(/^Bijlage 1 Salarisschalen — /, "")}</p>
    <div className="overflow-x-auto border border-slate-200 rounded-lg">
      <table className="text-xs w-full">
        <thead>
          <tr className="bg-slate-50">
            <th className="sticky left-0 z-10 bg-slate-50 text-left font-semibold text-slate-700 px-2.5 py-1.5 border-b border-r border-slate-200 whitespace-nowrap">Functiegroep</th>
            {tabel.kolommen.map((k) => (
              <th key={k} className="text-right font-semibold text-slate-700 px-2.5 py-1.5 border-b border-slate-200 whitespace-nowrap">{k}</th>
            ))}
            {tabel.heeft_bonus && <th className="text-right font-semibold text-slate-700 px-2.5 py-1.5 border-b border-slate-200 whitespace-nowrap">Bonus²</th>}
          </tr>
        </thead>
        <tbody>
          {tabel.rijen.map((r) => (
            <tr key={r.groep} className="border-b border-slate-100 last:border-0">
              <td className="sticky left-0 z-10 bg-white font-medium text-slate-800 px-2.5 py-1.5 border-r border-slate-200 whitespace-nowrap">{r.groep}</td>
              {tabel.kolommen.map((k) => (
                <td key={k} className={`px-2.5 py-1.5 text-right whitespace-nowrap ${r.maximum === k ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                  {r.cellen[k] ?? "—"}
                </td>
              ))}
              {tabel.heeft_bonus && <td className="px-2.5 py-1.5 text-right whitespace-nowrap text-slate-700">{r.bonus ?? "—"}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="text-[11px] text-slate-500 leading-relaxed">
      Kolommen: aantal periodieken (A = A-periodiek); het vetgedrukte bedrag is het schaalmaximum. — = niet van toepassing in deze functiegroep.
      {tabel.heeft_bonus && " ² Bonus bij uitstekende beoordeling (2,5% van het schaalmaximum)."}
    </p>
    <p className="text-[11px] text-slate-500 font-medium">
      Bron: CAO Zoetwarenindustrie 2024–2026{pagina ? `, pagina ${pagina}` : ""}
    </p>
  </div>
);

const Bijlage1 = ({ bijlage }) => {
  const [open, setOpen] = useState(null);
  return (
    <div id="cao-bijlage1" data-testid="cao-bijlage1" className="border border-[#EBD9BC] bg-[#FDF8EE] rounded-xl p-4 space-y-2">
      <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
        <Table2 className="w-4 h-4 text-[#B9772B]" /> {bijlage.titel} — per ingangsdatum
      </p>
      <p className="text-xs text-slate-600">Elke tabel geldt vanaf zijn eigen ingangsdatum. Bedragen uit verschillende data worden nooit gecombineerd.</p>
      <div className="space-y-1.5">
        {bijlage.tabellen.map((t, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-lg">
            <button data-testid={`cao-bijlage1-tabel-${i}`} onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium text-slate-700 hover:bg-slate-50">
              <span>{t.kop}</span>
              <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && (
              <div className="px-3 pb-3">
                {t.tabel
                  ? <B1Matrix tabel={t.tabel} pagina={t.pagina} index={i} />
                  : <BronBlok tekst={t.tekst} pagina={t.pagina} testid={`cao-bijlage1-bron-${i}`} />}
              </div>
            )}
          </div>
        ))}
      </div>
      {bijlage.link && (
        <Link to={bijlage.link.route} data-testid="cao-bijlage1-link"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#B9772B] hover:underline">
          {bijlage.link.label} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
};

const PensioenKaart = ({ pensioen }) => {
  const [bronOpen, setBronOpen] = useState(false);
  return (
    <div data-testid="cao-pensioen" className="border border-slate-200 rounded-xl bg-white px-4 py-3 space-y-2">
      <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Landmark className="w-4 h-4 text-[#B9772B]" /> {pensioen.vraag_titel}</p>
      <p className="text-xs text-slate-500">{pensioen.titel} — dit hoofdstuk heeft in de cao geen artikelnummers</p>
      <p className="text-sm text-slate-700 leading-relaxed">{pensioen.uitleg}</p>
      <button data-testid="cao-pensioen-bron-btn" onClick={() => setBronOpen(!bronOpen)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900">
        <BookOpenText className="w-3.5 h-3.5" /> Waar staat dat? {bronOpen ? "Verberg" : "Toon"} de volledige cao-tekst
      </button>
      {bronOpen && <BronBlok tekst={pensioen.brontekst} pagina={pensioen.pagina} testid="cao-pensioen-bron" />}
    </div>
  );
};

export default function CaoWijzer() {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(false);
  const [zoek, setZoek] = useState("");
  const [openOnderwerp, setOpenOnderwerp] = useState(null);
  const [openArtikel, setOpenArtikel] = useState(null);
  const [openBijlagen, setOpenBijlagen] = useState(false);
  const [openBijlage, setOpenBijlage] = useState(null);
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/cao-wijzer").then((r) => setData(r.data)).catch(() => setFout(true));
  }, []);

  useEffect(() => {
    const art = params.get("artikel");
    if (art && data) {
      const o = data.onderwerpen.find((x) => x.artikelen.some((k) => k.code === art));
      if (o) {
        setOpenOnderwerp(o.id);
        setOpenArtikel(art);
        setTimeout(() => document.getElementById(`cao-art-${art}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
      }
    }
  }, [params, data]);

  useEffect(() => {
    const b = params.get("bijlage");
    if (b && data) {
      setZoek("");
      if (b === "b1") {
        setOpenOnderwerp("loon");
        setTimeout(() => document.getElementById("cao-bijlage1")?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
      } else if ((data.bijlagen || []).some((x) => x.id === b)) {
        setOpenBijlagen(true);
        setOpenBijlage(b);
        setTimeout(() => document.getElementById(`cao-bijlage-${b}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
      }
    }
  }, [params, data]);

  const q = zoek.trim().toLowerCase();
  // Artikelnummer-herkenning: "artikel 2.5" / "art 2.5" / "art. 2.5" zoekt EXACT op code 2.5
  // (geen substring-zoektocht, anders matcht "artikel 25" op 3.25). Kale nummers zonder
  // "artikel" blijven gewone steekwoorden, zodat bedragen/percentages normaal blijven werken.
  const artMatch = q.match(/^art(?:ikel)?\.?\s*(\d+(?:\.\d+)?)$/);
  const artCode = artMatch ? artMatch[1] : null;
  const qc = q.replace(/\s+/g, "");
  const b1Zichtbaar = useMemo(() => {
    if (!data || !q || artCode) return false;
    return (data.bijlage1.zoektermen || []).some((t) => t.includes(q) || t.replace(/\s+/g, "").includes(qc));
  }, [data, q, qc, artCode]);
  const gefilterd = useMemo(() => {
    if (!data) return [];
    if (!q) return data.onderwerpen;
    return data.onderwerpen
      .map((o) => ({
        ...o,
        artikelen: artCode
          ? o.artikelen.filter((k) => k.code === artCode)
          : o.artikelen.filter((k) => k.zoektermen.some((t) => t.includes(q) || t.replace(/\s+/g, "").includes(qc))),
      }))
      .filter((o) => o.artikelen.length > 0 || (o.id === "loon" && b1Zichtbaar));
  }, [data, q, qc, artCode, b1Zichtbaar]);
  const bijlagenGefilterd = useMemo(() => {
    if (!data) return [];
    if (!q) return data.bijlagen || [];
    if (artCode) return [];
    return (data.bijlagen || []).filter((b) => b.zoektermen.some((t) => t.includes(q) || t.replace(/\s+/g, "").includes(qc)));
  }, [data, q, qc, artCode]);
  const geenResultaat = data && gefilterd.length === 0 && bijlagenGefilterd.length === 0;
  // Alleen zuivere artikelnotaties tellen als artikelbedoeling; kale nummers alleen bij
  // nul resultaten, zodat steekwoorden met cijfers ("90", "36") blijven werken.
  const artikelBedoeld = geenResultaat && q && (artCode || /^\d+(\.\d+)?$/.test(q)) ? (artCode || q) : null;
  const artikelSuggestie = useMemo(() => {
    if (!data || !artikelBedoeld || artikelBedoeld.includes(".")) return null;
    const kandidaten = data.onderwerpen.flatMap((o) => o.artikelen.map((k) => k.code))
      .filter((c) => c.replace(".", "") === artikelBedoeld);
    return kandidaten.length === 1 ? kandidaten[0] : null;
  }, [data, artikelBedoeld]);

  if (fout) return <p className="p-6 text-sm text-slate-600" data-testid="cao-fout">De CAO-wijzer kon niet worden geladen. Probeer het later opnieuw.</p>;
  if (!data) return <p className="p-6 text-sm text-slate-500" data-testid="cao-laden">CAO-wijzer laden…</p>;

  const totaal = gefilterd.reduce((n, o) => n + o.artikelen.length, 0);
  const bijlagenTeller = q ? bijlagenGefilterd.length + (b1Zichtbaar ? 1 : 0) : 0;
  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="cao-wijzer-pagina">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold font-heading text-[#3A281B] flex items-center gap-2">
          <ScrollText className="w-6 h-6 text-[#B9772B]" /> CAO-wijzer
        </h1>
        <p className="text-sm text-slate-600" data-testid="cao-versiekop">
          {data.versie.titel} · geldig {data.versie.looptijd}
        </p>
        <p className="text-xs text-slate-500">Officiële versie: {data.versie.versie_detail}. Zoek zelf een onderwerp op en lees precies wat er in de cao staat.</p>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="cao-zoek-input" value={zoek} onChange={(e) => setZoek(e.target.value)}
          placeholder="Zoek op artikelnummer of steekwoord, bijvoorbeeld artikel 2.5, ziekte of verlof"
          className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:border-[#B9772B] focus:ring-2 focus:ring-[#B9772B]/40" />
      </div>
      <p className="text-xs text-slate-500" data-testid="cao-zoek-hulplijn">
        Zoek hier op een artikelnummer of steekwoord. Voor langere vragen gebruik je{" "}
        <Link to="/" className="text-[#B9772B] font-medium hover:underline">Stel een vraag</Link>.
      </p>
      {q && <p className="text-xs text-slate-500" data-testid="cao-teller">{totaal} artikel{totaal === 1 ? "" : "en"}{bijlagenTeller > 0 ? ` en ${bijlagenTeller} bijlage${bijlagenTeller === 1 ? "" : "n"}` : ""} gevonden</p>}

      {geenResultaat && (artikelBedoeld ? (
        <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="cao-artikel-niet-gevonden">
          {artikelSuggestie ? (
            <>Artikel {artikelBedoeld} bestaat niet. Bedoelde je{" "}
              <button data-testid="cao-artikel-suggestie" onClick={() => setZoek(artikelSuggestie)}
                className="text-[#B9772B] font-medium hover:underline">artikel {artikelSuggestie}</button>?</>
          ) : (
            <>Dit artikelnummer bestaat niet.</>
          )}
        </div>
      ) : (
        <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="cao-geen-resultaat">
          Geen artikelen gevonden voor "{zoek}". Probeer een ander woord, of stel je vraag via <Link to="/" className="text-[#B9772B] font-medium hover:underline">Vraag &amp; antwoord</Link>.
        </div>
      ))}

      {gefilterd.map((o) => {
        const isOpen = q ? true : openOnderwerp === o.id;
        return (
          <section key={o.id} className="border border-slate-200 rounded-2xl bg-white/70 overflow-hidden">
            <button data-testid={`cao-onderwerp-toggle-${o.id}`}
              onClick={() => setOpenOnderwerp(isOpen && !q ? null : o.id)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors">
              <div>
                <p className="text-base font-semibold font-heading text-[#3A281B]">{o.titel}</p>
                <p className="text-xs text-slate-500 mt-0.5">{o.artikelen.length} artikel{o.artikelen.length === 1 ? "" : "en"}{o.id === "loon" ? " + salaristabellen" : ""}{o.id === "gezond" && data.pensioen ? " + pensioen" : ""}</p>
              </div>
              <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="px-4 pb-4 space-y-2">
                {o.artikelen.map((k) => (
                  <ArtikelKaart key={k.code} kaart={k} open={openArtikel === k.code}
                    onToggle={() => setOpenArtikel(openArtikel === k.code ? null : k.code)} />
                ))}
                {o.id === "loon" && (!q || b1Zichtbaar) && <Bijlage1 bijlage={data.bijlage1} />}
                {o.id === "gezond" && !q && data.pensioen && <PensioenKaart pensioen={data.pensioen} />}
                {o.zie_ook.length > 0 && !q && (
                  <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`cao-zie-ook-${o.id}`}>
                    <span className="text-[11px] text-slate-500 py-1">Zie ook:</span>
                    {o.zie_ook.map((z) => (
                      <Link key={z.code} to={`/cao?artikel=${z.code}`} data-testid={`cao-zie-ook-link-${o.id}-${z.code}`}
                        className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-[#FBF3E3] hover:text-[#8a5a20] transition-colors">
                        {z.vraag_titel} → {z.onderwerp_titel}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}

      {bijlagenGefilterd.length > 0 && (
        <section data-testid="cao-bijlagen-sectie" className="border border-slate-200 rounded-2xl bg-white/70 overflow-hidden">
          <button data-testid="cao-bijlagen-toggle"
            onClick={() => setOpenBijlagen(q ? true : !openBijlagen)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors">
            <div>
              <p className="text-base font-semibold font-heading text-[#3A281B]">Bijlagen bij de cao</p>
              <p className="text-xs text-slate-500 mt-0.5">{bijlagenGefilterd.length} bijlage{bijlagenGefilterd.length === 1 ? "" : "n"} · de salaristabellen (Bijlage 1) staan bij "Loon en salarisschalen"</p>
            </div>
            <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${(q || openBijlagen) ? "rotate-180" : ""}`} />
          </button>
          {(q || openBijlagen) && (
            <div className="px-4 pb-4 space-y-2">
              {bijlagenGefilterd.map((b) => (
                <BijlageKaart key={b.id} b={b} open={openBijlage === b.id}
                  onToggle={() => setOpenBijlage(openBijlage === b.id ? null : b.id)} />
              ))}
            </div>
          )}
        </section>
      )}

      <p className="text-[11px] text-slate-400 pb-4">
        De teksten onder "Waar staat dat?" zijn letterlijk overgenomen uit de {data.versie.titel}. Aan de korte uitleg kunnen geen rechten worden ontleend; de cao-tekst is altijd leidend.
      </p>
    </div>
  );
}
